// G-1 teaming + competition lists. Each row is a registry Company link —
// names display from ListCompanyProfiles after the link is stored.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCompanyProfiles } from '../Delivery/Programs/referenceDataHooks';
import { ASSERTION_STATUSES, assertionStatusWording, COMPETITION_ROLES, CONFIDENCE_LEVELS, EVIDENCE_CLAIM_TYPES, EVIDENCE_SOURCE_KINDS, EVIDENCE_STANCES, formatTs, INCLUSION_BASES, LIKELIHOOD_BANDS, labelOf } from './constants';
import { useCompetitiveField, useDeleteBlackHatInput, useDeleteCaptureCompetitionLink, useDeleteCaptureTeamingLink, useDeleteCompetitiveFieldParticipant, useRecordCompetitiveFieldAssessment, useRecordCompetitorEvidence, useUpsertBlackHatInput, useUpsertCaptureCompetitionLink, useUpsertCaptureTeamingLink, useUpsertCompetitiveFieldParticipant, useUpsertCompetitiveFieldStrategy } from './hooks';
function dateInputValue(ts) {
    if (!ts) return '';
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
// Assertion status pill colors — hypothesis reads as caution, never as fact.
const ASSERTION_STYLE = {
    hypothesis: 'bg-amber-50 text-amber-800',
    corroborated: 'bg-indigo-50 text-indigo-700',
    established: 'bg-green-50 text-green-700'
};
// Evidence stance pill colors — contradicting evidence must be visibly
// distinguishable from supporting evidence.
const STANCE_STYLE = {
    supports: 'bg-green-50 text-green-700',
    contradicts: 'bg-red-50 text-red-700'
};
export function CaptureCompanyLinksPanel({ captureProjectId, teamingLinks, competitionLinks, correlationId }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid gap-4 lg:grid-cols-2",
        children: [
            /*#__PURE__*/ _jsx(TeamingList, {
                captureProjectId: captureProjectId,
                links: teamingLinks,
                correlationId: correlationId
            }),
            /*#__PURE__*/ _jsx(CompetitionList, {
                captureProjectId: captureProjectId,
                links: competitionLinks,
                correlationId: correlationId
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "lg:col-span-2",
                children: /*#__PURE__*/ _jsx(CompetitiveFieldSection, {
                    captureProjectId: captureProjectId,
                    correlationId: correlationId
                })
            })
        ]
    });
}
function TeamingList({ captureProjectId, links, correlationId }) {
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const upsert = useUpsertCaptureTeamingLink(correlationId);
    const remove = useDeleteCaptureTeamingLink(correlationId);
    const [companyId, setCompanyId] = useState('');
    const [workshare, setWorkshare] = useState('');
    const add = ()=>{
        if (!companyId) return;
        upsert.mutate({
            captureProjectId,
            companyProfileId: companyId,
            workshare
        }, {
            onSuccess: ()=>{
                setCompanyId('');
                setWorkshare('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.teamingPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Teaming"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Company"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: companyId || undefined,
                                        onValueChange: setCompanyId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.teamingCompanySelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: c.id,
                                                        children: c.name
                                                    }, c.id))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-24 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Workshare"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: workshare,
                                        onChange: (e)=>setWorkshare(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: add,
                                disabled: !companyId || upsert.isPending,
                                "data-testid": Testids.capture.teamingAddButton,
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                children: "Add"
                            })
                        ]
                    }),
                    links.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No teammates linked yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: links.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                "data-testid": Testids.capture.teamingRow,
                                "data-link-id": l.id,
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            names.get(l.companyProfileId) ?? l.companyProfileId,
                                            l.workshare ? ` · ${l.workshare}` : '',
                                            l.nda ? ' · NDA' : '',
                                            l.ta ? ' · TA' : ''
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        "data-testid": Testids.capture.teamingRemoveButton,
                                        onClick: ()=>remove.mutate({
                                                captureProjectId,
                                                id: l.id
                                            }),
                                        iconLeft: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        children: "Remove"
                                    })
                                ]
                            }, l.id))
                    })
                ]
            })
        ]
    });
}
function CompetitionList({ captureProjectId, links, correlationId }) {
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const upsert = useUpsertCaptureCompetitionLink(correlationId);
    const remove = useDeleteCaptureCompetitionLink(correlationId);
    const [companyId, setCompanyId] = useState('');
    const [role, setRole] = useState('incumbent');
    const add = ()=>{
        if (!companyId) return;
        upsert.mutate({
            captureProjectId,
            companyProfileId: companyId,
            role
        }, {
            onSuccess: ()=>setCompanyId('')
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.competitionPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Competition"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Company"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: companyId || undefined,
                                        onValueChange: setCompanyId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitionCompanySelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: c.id,
                                                        children: c.name
                                                    }, c.id))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Role"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: role,
                                        onValueChange: setRole,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitionRoleSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: COMPETITION_ROLES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: add,
                                disabled: !companyId || upsert.isPending,
                                "data-testid": Testids.capture.competitionAddButton,
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                children: "Add"
                            })
                        ]
                    }),
                    links.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No competitors linked yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: links.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                "data-testid": Testids.capture.competitionRow,
                                "data-link-id": l.id,
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            names.get(l.companyProfileId) ?? l.companyProfileId,
                                            ` · ${labelOf(COMPETITION_ROLES, l.role)}`
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        "data-testid": Testids.capture.competitionRemoveButton,
                                        onClick: ()=>remove.mutate({
                                                captureProjectId,
                                                id: l.id
                                            }),
                                        iconLeft: /*#__PURE__*/ _jsx(X, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        children: "Remove"
                                    })
                                ]
                            }, l.id))
                    })
                ]
            })
        ]
    });
}
// Evidence-backed competitive field (SAD-495): the participant list, its
// qualitative likelihood assessment + evidence, and its proposed strategy /
// Black Hat inputs are three distinct areas — never blended so a hypothesis
// cannot be mistaken for an established fact (B3.4-07.2, B3.4-14.3). No
// numeric price/rate field is ever requested here (B3.4-08).
function CompetitiveFieldSection({ captureProjectId, correlationId }) {
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const field = useCompetitiveField(captureProjectId);
    const upsertParticipant = useUpsertCompetitiveFieldParticipant(correlationId);
    const removeParticipant = useDeleteCompetitiveFieldParticipant(correlationId);
    const [companyId, setCompanyId] = useState('');
    const [inclusionBasis, setInclusionBasis] = useState(INCLUSION_BASES[0].value);
    const [basisNote, setBasisNote] = useState('');
    const addParticipant = ()=>{
        if (!companyId) return;
        upsertParticipant.mutate({
            captureProjectId,
            companyProfileId: companyId,
            inclusionBasis,
            basisNote
        }, {
            onSuccess: ()=>{
                setCompanyId('');
                setBasisNote('');
            }
        });
    };
    const entries = field.data?.entries ?? [];
    const blackHatInputs = field.data?.blackHatInputs ?? [];
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.competitiveFieldPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Competitive field"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Evidence-backed read on who else is chasing this — participation, likelihood, and strategy are recorded separately, and every likelihood starts as a hypothesis until evidence corroborates or establishes it."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Company"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: companyId || undefined,
                                        onValueChange: setCompanyId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveFieldCompanySelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: c.id,
                                                        children: c.name
                                                    }, c.id))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-48 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Basis of inclusion"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: inclusionBasis,
                                        onValueChange: setInclusionBasis,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveFieldInclusionBasisSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: INCLUSION_BASES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Basis note"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: basisNote,
                                        onChange: (e)=>setBasisNote(e.target.value),
                                        placeholder: "Why this company is in the field",
                                        "data-testid": Testids.capture.competitiveFieldBasisNoteInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: addParticipant,
                                disabled: !companyId || upsertParticipant.isPending,
                                "data-testid": Testids.capture.competitiveFieldAddButton,
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                children: "Add participant"
                            })
                        ]
                    }),
                    entries.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No competitive field participants yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-3",
                        children: entries.map((entry)=>/*#__PURE__*/ _jsx(CompetitiveFieldParticipantRow, {
                                captureProjectId: captureProjectId,
                                correlationId: correlationId,
                                entry: entry,
                                companyName: names.get(entry.participant?.companyProfileId ?? '') ?? entry.participant?.companyProfileId ?? '',
                                blackHatInputs: blackHatInputs.filter((b)=>b.participantId === entry.participant?.id),
                                onRemove: ()=>entry.participant && removeParticipant.mutate({
                                        captureProjectId,
                                        id: entry.participant.id
                                    })
                            }, entry.participant?.id))
                    })
                ]
            })
        ]
    });
}
function CompetitiveFieldParticipantRow({ captureProjectId, correlationId, entry, companyName, blackHatInputs, onRemove }) {
    const participant = entry.participant;
    const assessment = entry.currentAssessment;
    const strategy = entry.strategy;
    const recordAssessment = useRecordCompetitiveFieldAssessment(correlationId);
    const recordEvidence = useRecordCompetitorEvidence(correlationId);
    const upsertStrategy = useUpsertCompetitiveFieldStrategy(correlationId);
    const upsertBlackHat = useUpsertBlackHatInput(correlationId);
    const removeBlackHat = useDeleteBlackHatInput(correlationId);
    const [likelihoodBand, setLikelihoodBand] = useState(assessment?.likelihoodBand || LIKELIHOOD_BANDS[0].value);
    const [assertionStatus, setAssertionStatus] = useState(assessment?.assertionStatus || ASSERTION_STATUSES[0].value);
    const [confidence, setConfidence] = useState(assessment?.confidence || CONFIDENCE_LEVELS[0].value);
    const [rationale, setRationale] = useState('');
    const [asOfDate, setAsOfDate] = useState(dateInputValue(assessment?.asOfDate));
    const [sourceKind, setSourceKind] = useState(EVIDENCE_SOURCE_KINDS[0].value);
    const [sourceRef, setSourceRef] = useState('');
    const [sourceDate, setSourceDate] = useState('');
    const [claimType, setClaimType] = useState(EVIDENCE_CLAIM_TYPES[0].value);
    const [stance, setStance] = useState(EVIDENCE_STANCES[0].value);
    const [evidenceConfidence, setEvidenceConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const [evidenceRationale, setEvidenceRationale] = useState('');
    const [inferenceAssumptions, setInferenceAssumptions] = useState('');
    const [positioning, setPositioning] = useState(strategy?.positioning ?? '');
    const [discriminators, setDiscriminators] = useState(strategy?.discriminators ?? '');
    const [response, setResponse] = useState(strategy?.response ?? '');
    const [advantages, setAdvantages] = useState('');
    const [vulnerabilities, setVulnerabilities] = useState('');
    const [likelyApproaches, setLikelyApproaches] = useState('');
    const [ourResponse, setOurResponse] = useState('');
    const [openQuestions, setOpenQuestions] = useState('');
    if (!participant) return null;
    const submitAssessment = ()=>{
        recordAssessment.mutate({
            captureProjectId,
            participantId: participant.id,
            likelihoodBand,
            assertionStatus,
            confidence,
            rationale,
            asOfDate: parseDateInput(asOfDate)
        });
    };
    const submitEvidence = ()=>{
        recordEvidence.mutate({
            captureProjectId,
            participantId: participant.id,
            sourceKind,
            sourceRef,
            sourceDate: parseDateInput(sourceDate),
            claimType,
            stance,
            confidence: evidenceConfidence,
            rationale: evidenceRationale,
            inferenceAssumptions
        }, {
            onSuccess: ()=>{
                setSourceRef('');
                setSourceDate('');
                setEvidenceRationale('');
                setInferenceAssumptions('');
            }
        });
    };
    const submitStrategy = ()=>{
        upsertStrategy.mutate({
            captureProjectId,
            id: strategy?.id,
            participantId: participant.id,
            positioning,
            discriminators,
            response
        });
    };
    const submitBlackHat = ()=>{
        upsertBlackHat.mutate({
            captureProjectId,
            participantId: participant.id,
            competitorAdvantages: advantages,
            competitorVulnerabilities: vulnerabilities,
            likelyApproaches,
            ourResponse,
            openQuestions
        }, {
            onSuccess: ()=>{
                setAdvantages('');
                setVulnerabilities('');
                setLikelyApproaches('');
                setOurResponse('');
                setOpenQuestions('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-3 rounded-md border p-3 text-sm",
        "data-testid": Testids.capture.competitiveParticipantRow,
        "data-participant-id": participant.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: companyName
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    ' · ',
                                    labelOf(INCLUSION_BASES, participant.inclusionBasis)
                                ]
                            }),
                            participant.basisNote && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: participant.basisNote
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        size: "sm",
                        "data-testid": Testids.capture.competitiveParticipantRemoveButton,
                        onClick: onRemove,
                        iconLeft: /*#__PURE__*/ _jsx(X, {
                            className: "h-3.5 w-3.5"
                        }),
                        children: "Remove"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveLikelihoodArea,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${ASSERTION_STYLE[assessment?.assertionStatus ?? 'hypothesis'] ?? ASSERTION_STYLE.hypothesis}`,
                                "data-testid": Testids.capture.competitiveAssertionBadge,
                                children: assertionStatusWording(assessment?.assertionStatus ?? 'hypothesis')
                            }),
                            assessment && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    labelOf(LIKELIHOOD_BANDS, assessment.likelihoodBand),
                                    " likelihood ·",
                                    ' ',
                                    labelOf(CONFIDENCE_LEVELS, assessment.confidence),
                                    " confidence · as of",
                                    ' ',
                                    formatTs(assessment.asOfDate)
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Likelihood"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: likelihoodBand,
                                        onValueChange: setLikelihoodBand,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveLikelihoodBandSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: LIKELIHOOD_BANDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Assertion status"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: assertionStatus,
                                        onValueChange: setAssertionStatus,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveAssertionStatusSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: ASSERTION_STATUSES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-32 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Confidence"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: confidence,
                                        onValueChange: setConfidence,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveConfidenceSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "As of"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        type: "date",
                                        value: asOfDate,
                                        onChange: (e)=>setAsOfDate(e.target.value),
                                        "data-testid": Testids.capture.competitiveAssessmentDateInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Rationale"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: rationale,
                                        onChange: (e)=>setRationale(e.target.value),
                                        placeholder: "Why this band and status",
                                        "data-testid": Testids.capture.competitiveAssessmentRationaleInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: submitAssessment,
                                disabled: recordAssessment.isPending,
                                "data-testid": Testids.capture.competitiveAssessButton,
                                children: assessment ? 'Revise' : 'Assess'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-medium",
                                children: "Evidence"
                            }),
                            entry.evidence.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No evidence recorded."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-1",
                                children: entry.evidence.map((e)=>/*#__PURE__*/ _jsxs("li", {
                                        className: "rounded border px-2 py-1 text-xs",
                                        "data-testid": Testids.capture.competitiveEvidenceRow,
                                        "data-stance": e.stance,
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `mr-1 inline-flex items-center rounded-full px-1.5 py-0.5 ${STANCE_STYLE[e.stance] ?? STANCE_STYLE.supports}`,
                                                children: labelOf(EVIDENCE_STANCES, e.stance)
                                            }),
                                            labelOf(EVIDENCE_CLAIM_TYPES, e.claimType),
                                            " ·",
                                            ' ',
                                            labelOf(EVIDENCE_SOURCE_KINDS, e.sourceKind),
                                            e.sourceRef ? ` (${e.sourceRef})` : '',
                                            " · ",
                                            formatTs(e.sourceDate),
                                            " ·",
                                            ' ',
                                            labelOf(CONFIDENCE_LEVELS, e.confidence),
                                            " confidence",
                                            e.rationale ? ` — ${e.rationale}` : ''
                                        ]
                                    }, e.id))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Source"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: sourceKind,
                                                onValueChange: setSourceKind,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceSourceKindSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_SOURCE_KINDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[140px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Reference"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: sourceRef,
                                                onChange: (e)=>setSourceRef(e.target.value),
                                                placeholder: "Link or citation",
                                                "data-testid": Testids.capture.competitiveEvidenceSourceRefInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-36 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Date"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                value: sourceDate,
                                                onChange: (e)=>setSourceDate(e.target.value),
                                                "data-testid": Testids.capture.competitiveEvidenceDateInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-44 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Claim type"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: claimType,
                                                onValueChange: setClaimType,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceClaimTypeSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_CLAIM_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-32 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Stance"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: stance,
                                                onValueChange: setStance,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceStanceSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_STANCES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-32 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Confidence"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: evidenceConfidence,
                                                onValueChange: setEvidenceConfidence,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceConfidenceSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[160px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Rationale"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: evidenceRationale,
                                                onChange: (e)=>setEvidenceRationale(e.target.value),
                                                placeholder: "What this evidence shows",
                                                "data-testid": Testids.capture.competitiveEvidenceRationaleInput
                                            })
                                        ]
                                    }),
                                    claimType === 'rate_or_price_inference' && /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[160px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Inference & assumptions"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: inferenceAssumptions,
                                                onChange: (e)=>setInferenceAssumptions(e.target.value),
                                                placeholder: "Labeled inference and the assumptions behind it",
                                                "data-testid": Testids.capture.competitiveEvidenceInferenceAssumptionsInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        onClick: submitEvidence,
                                        disabled: recordEvidence.isPending,
                                        "data-testid": Testids.capture.competitiveEvidenceAddButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        children: "Add evidence"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveStrategyArea,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Proposed strategy"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: positioning,
                        onChange: (e)=>setPositioning(e.target.value),
                        placeholder: "Positioning",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyPositioningInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: discriminators,
                        onChange: (e)=>setDiscriminators(e.target.value),
                        placeholder: "Discriminators",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyDiscriminatorsInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: response,
                        onChange: (e)=>setResponse(e.target.value),
                        placeholder: "Response",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyResponseInput
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submitStrategy,
                            disabled: upsertStrategy.isPending,
                            "data-testid": Testids.capture.competitiveStrategySaveButton,
                            children: "Save strategy"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveBlackHatArea,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Black Hat"
                    }),
                    blackHatInputs.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "No Black Hat inputs yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: blackHatInputs.map((b)=>/*#__PURE__*/ _jsxs("li", {
                                className: "space-y-1 rounded border p-2 text-xs",
                                "data-testid": Testids.capture.competitiveBlackHatRow,
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Advantages:"
                                            }),
                                            " ",
                                            b.competitorAdvantages || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Vulnerabilities:"
                                            }),
                                            ' ',
                                            b.competitorVulnerabilities || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Likely approaches:"
                                            }),
                                            ' ',
                                            b.likelyApproaches || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Our response:"
                                            }),
                                            " ",
                                            b.ourResponse || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Open questions:"
                                            }),
                                            " ",
                                            b.openQuestions || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex justify-end",
                                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            "data-testid": Testids.capture.competitiveBlackHatRemoveButton,
                                            onClick: ()=>removeBlackHat.mutate({
                                                    captureProjectId,
                                                    id: b.id
                                                }),
                                            iconLeft: /*#__PURE__*/ _jsx(X, {
                                                className: "h-3.5 w-3.5"
                                            }),
                                            children: "Remove"
                                        })
                                    })
                                ]
                            }, b.id))
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: advantages,
                        onChange: (e)=>setAdvantages(e.target.value),
                        placeholder: "Competitor advantages",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatAdvantagesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: vulnerabilities,
                        onChange: (e)=>setVulnerabilities(e.target.value),
                        placeholder: "Competitor vulnerabilities",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatVulnerabilitiesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: likelyApproaches,
                        onChange: (e)=>setLikelyApproaches(e.target.value),
                        placeholder: "Likely approaches",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatApproachesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: ourResponse,
                        onChange: (e)=>setOurResponse(e.target.value),
                        placeholder: "Our response",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatResponseInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: openQuestions,
                        onChange: (e)=>setOpenQuestions(e.target.value),
                        placeholder: "Open questions",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatQuestionsInput
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submitBlackHat,
                            disabled: upsertBlackHat.isPending,
                            "data-testid": Testids.capture.competitiveBlackHatAddButton,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-3.5 w-3.5"
                            }),
                            children: "Add Black Hat input"
                        })
                    })
                ]
            })
        ]
    });
}
function useCompanyNameMap(profiles) {
    return useMemo(()=>{
        const m = new Map();
        for (const p of profiles ?? []){
            m.set(p.id, p.name);
        }
        return m;
    }, [
        profiles
    ]);
}
